import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  Mail,
  Search,
  RefreshCcw,
  Eye,
  User,
  Hash,
  AlignLeft,
  Info,
  Calendar,
  Phone,
} from "lucide-react";
import axios from "axios";

// Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const API_URL = "https://beaconsms.com/oldsite/api/global_messages_api.php";

const GlobalMessageHistory = () => {
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.id === "71";

  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?adminId=71`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    document.title = "Global Message Logs - beaconSMS";
    fetchMessages();
  }, [fetchMessages]);

  const handleView = (msg) => {
    // Logic to count recipients: split by comma and remove empty entries
    const count = msg.msg_destination
      ? msg.msg_destination.split(",").filter((n) => n.trim() !== "").length
      : 0;

    setSelectedMsg({ ...msg, recipientCount: count });
    setShowModal(true);
  };

  const filteredMessages = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return messages.filter(
      (m) =>
        (m.username || "").toLowerCase().includes(term) ||
        (m.msg_destination || "").includes(term) ||
        (m.msg_message || "").toLowerCase().includes(term),
    );
  }, [messages, searchTerm]);

  if (authLoading)
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
      </div>
    );

  if (!isAdmin) {
    return (
      <Container className='mt-5 text-center p-5 bg-white border rounded shadow-sm'>
        <h2 className='fw-bold text-danger'>Access Denied</h2>
        <p className='text-muted'>
          You do not have administrative privileges to view system-wide logs.
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        {/* Sidebar */}
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        {/* Main Content */}
        <Col md='9'>
          <div className='bg-white p-4 border rounded shadow-sm min-vh-100'>
            <div className='d-flex justify-content-between align-items-center mb-4 border-bottom pb-3'>
              <div className='d-flex align-items-center text-primary'>
                <Mail className='me-2' size={24} />
                <h4 className='mb-0 fw-bold'>System-Wide Message History</h4>
              </div>
              <Button
                variant='link'
                onClick={fetchMessages}
                className='text-decoration-none p-0'
              >
                <RefreshCcw
                  size={20}
                  className={loading ? "fa-spin text-primary" : "text-muted"}
                />
              </Button>
            </div>

            <InputGroup className='mb-4 shadow-sm'>
              <InputGroup.Text className='bg-white border-end-0'>
                <Search size={18} className='text-muted' />
              </InputGroup.Text>
              <Form.Control
                className='border-start-0 ps-0'
                placeholder='Search by sender, recipient, or content...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <div className='table-responsive'>
              <Table hover className='align-middle border small'>
                <thead
                  className='bg-light text-uppercase'
                  style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                >
                  <tr>
                    <th>Sender</th>
                    <th>Recipient</th>
                    <th style={{ width: "35%" }}>Message Summary</th>
                    <th>Status</th>
                    <th className='text-end'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan='5' className='text-center py-5'>
                        <Spinner animation='border' variant='primary' />
                      </td>
                    </tr>
                  ) : filteredMessages.length > 0 ? (
                    filteredMessages.map((m, i) => (
                      <tr key={i}>
                        <td className='fw-bold text-primary'>@{m.username}</td>
                        <td
                          className='text-muted'
                          style={{
                            maxWidth: "120px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.msg_destination}
                        </td>
                        <td
                          className='text-muted'
                          style={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.msg_message}
                        </td>
                        <td>
                          <Badge
                            bg={
                              m.msg_status === "Sent"
                                ? "success-subtle"
                                : "warning-subtle"
                            }
                            className={
                              m.msg_status === "Sent"
                                ? "text-success border border-success-subtle"
                                : "text-warning border border-warning-subtle"
                            }
                          >
                            {m.msg_status}
                          </Badge>
                        </td>
                        <td className='text-end'>
                          <Button
                            variant='primary'
                            size='sm'
                            className='px-3 fw-bold'
                            onClick={() => handleView(m)}
                          >
                            <Eye size={14} className='me-1' /> View
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='5' className='text-center py-4 text-muted'>
                        No message records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>

      {/* --- Detail Modal --- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size='lg'
        centered
      >
        <Modal.Header closeButton className='bg-light'>
          <Modal.Title className='h6 fw-bold text-primary'>
            Message Log Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='p-4'>
          {selectedMsg && (
            <Row className='g-4'>
              <Col md={6}>
                <div className='mb-3'>
                  <label className='small text-uppercase fw-bold text-muted d-block mb-1'>
                    <User size={12} className='me-1' /> Sender Account
                  </label>
                  <div className='p-2 border rounded bg-light fw-bold text-primary'>
                    @{selectedMsg.username}
                  </div>
                </div>
                <div className='mb-3'>
                  <label className='small text-uppercase fw-bold text-muted d-block mb-1'>
                    <Calendar size={12} className='me-1' /> Timestamp
                  </label>
                  <div className='p-2 border rounded'>
                    {selectedMsg.msg_created}
                  </div>
                </div>
                <div>
                  <label className='small text-uppercase fw-bold text-muted d-block mb-1'>
                    <Info size={12} className='me-1' /> Delivery & Billing
                  </label>
                  <div className='p-2 border rounded d-flex justify-content-between align-items-center'>
                    <Badge
                      bg={
                        selectedMsg.msg_status === "Sent"
                          ? "success"
                          : "warning"
                      }
                    >
                      {selectedMsg.msg_status}
                    </Badge>
                    <span className='fw-bold text-danger'>
                      {selectedMsg.msg_units} Units Deducted
                    </span>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className='mb-3'>
                  <div className='d-flex justify-content-between align-items-center mb-1'>
                    <label className='small text-uppercase fw-bold text-muted mb-0'>
                      <Phone size={12} className='me-1' /> Recipient List
                    </label>
                    <Badge
                      bg='primary'
                      className='px-2'
                      style={{ fontSize: "10px" }}
                    >
                      {selectedMsg.recipientCount} Recipients
                    </Badge>
                  </div>
                  <div
                    className='p-2 border rounded bg-light overflow-auto'
                    style={{
                      maxHeight: "120px",
                      fontSize: "12px",
                      wordBreak: "break-all",
                      lineHeight: "1.5",
                    }}
                  >
                    {selectedMsg.msg_destination}
                  </div>
                </div>
                <div>
                  <label className='small text-uppercase fw-bold text-muted d-block mb-1'>
                    <AlignLeft size={12} className='me-1' /> Full Message Text
                  </label>
                  <div
                    className='p-3 border rounded bg-white shadow-sm'
                    style={{
                      whiteSpace: "pre-wrap",
                      minHeight: "100px",
                      fontSize: "14px",
                      borderLeft: "4px solid #0d6efd",
                    }}
                  >
                    {selectedMsg.msg_message}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className='bg-light'>
          <Button
            variant='secondary'
            onClick={() => setShowModal(false)}
            className='px-4'
          >
            Close Log
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </Container>
  );
};

export default GlobalMessageHistory;
